import { getApiUrl } from "src/env";
import type { CancellationReason, Invite, QueueEntry, Shift } from "src/api/types";

const API_URL = getApiUrl();

function jsonHeaders(token: string): HeadersInit {
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

function authHeaders(token: string): HeadersInit {
  return { Authorization: `Bearer ${token}` };
}

async function parseError(res: Response): Promise<string> {
  const body = (await res.json().catch(() => ({}))) as { error?: string };
  return body.error ?? "unknown_error";
}

export async function getQueue(token: string): Promise<QueueEntry[]> {
  const res = await fetch(`${API_URL}/queue`, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(await parseError(res));
  const data = (await res.json()) as { entries: QueueEntry[] };
  return data.entries;
}

export async function getMe(token: string): Promise<{ id: string; email: string }> {
  const res = await fetch(`${API_URL}/staff/me`, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{ id: string; email: string }>;
}

export async function getShifts(token: string): Promise<Shift[]> {
  const res = await fetch(`${API_URL}/shifts`, { headers: authHeaders(token) });
  if (!res.ok) throw new Error(await parseError(res));
  const data = (await res.json()) as { shifts: Shift[] };
  return data.shifts;
}

export async function openShift(token: string): Promise<Shift> {
  const res = await fetch(`${API_URL}/shifts`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify({ type: "CMA" }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<Shift>;
}

export async function closeShift(token: string, shiftId: string): Promise<Shift> {
  const res = await fetch(`${API_URL}/shifts/${shiftId}/close`, {
    method: "POST",
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<Shift>;
}

export async function callNext(token: string): Promise<QueueEntry | null> {
  const res = await fetch(`${API_URL}/queue/call`, {
    method: "POST",
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error(await parseError(res));
  const data = (await res.json()) as { entry: QueueEntry | null };
  return data.entry;
}

export async function completeEntry(token: string, entryId: string): Promise<QueueEntry> {
  const res = await fetch(`${API_URL}/queue/${entryId}/complete`, {
    method: "POST",
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<QueueEntry>;
}

export async function cancelEntry(
  token: string,
  entryId: string,
  reason: CancellationReason,
  note?: string,
): Promise<QueueEntry> {
  const res = await fetch(`${API_URL}/queue/${entryId}/cancel`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify({ reason, note }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<QueueEntry>;
}

export async function createInvite(token: string, patientId: string): Promise<Invite> {
  const res = await fetch(`${API_URL}/invites`, {
    method: "POST",
    headers: jsonHeaders(token),
    body: JSON.stringify({ patient_id: patientId }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<Invite>;
}

export async function sendInviteEmail(token: string, shortCode: string): Promise<void> {
  const res = await fetch(`${API_URL}/invites/${shortCode}/email`, {
    method: "POST",
    headers: authHeaders(token),
  });
  if (!res.ok) throw new Error(await parseError(res));
}

export function staffStreamURL(): string {
  return `${API_URL}/staff/stream`;
}
